import { useEffect } from 'react';

import {
  AlertCircle,
  Calendar,
  FileText,
  LinkIcon,
  Mail,
  MessageCircle,
  Settings,
  Upload,
  Users,
  X,
} from 'lucide-react';
import { NavLink } from 'react-router';

import { cn } from '@/lib/utils';
import { useSidebarStore } from '@/shared/stores/sidebarStore';

import chartaiLogoLight from '@/assets/chartai-logo-light.svg';

interface NavItem {
  icon: typeof Calendar;
  label: string;
  to: string;
  badge?: number;
}

const SECTIONS: readonly { items: readonly NavItem[] }[] = [
  {
    items: [
      { icon: Calendar, label: 'Today', to: '/dashboard/appointments' },
      { icon: Users, label: 'Patients', to: '/dashboard/patients' },
    ],
  },
  {
    items: [
      { icon: AlertCircle, label: 'Review Queue', to: '/dashboard/needs-review', badge: 4 },
      { icon: FileText, label: 'Templates', to: '/dashboard/templates' },
      { icon: Mail, label: 'Referral Letters', to: '/dashboard/referrals' },
    ],
  },
  {
    items: [
      { icon: Upload, label: 'Exports / Activity', to: '/dashboard/exports' },
      { icon: LinkIcon, label: 'Integrations', to: '/dashboard/integrations' },
    ],
  },
  {
    items: [
      { icon: Settings, label: 'Settings', to: '/dashboard/settings' },
    ],
  },
];

export function MobileSidebar() {
  const mobileOpen = useSidebarStore((s) => s.mobileOpen);
  const closeMobile = useSidebarStore((s) => s.closeMobile);

  useEffect(() => {
    if (!mobileOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMobile();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('overflow-hidden');

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('overflow-hidden');
    };
  }, [mobileOpen, closeMobile]);

  if (!mobileOpen) return null;

  const renderItems = (items: readonly NavItem[]) =>
    items.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        end
        onClick={closeMobile}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
            isActive
              ? 'bg-sidebar-active text-white'
              : 'text-sidebar-foreground/80 hover:bg-sidebar-hover hover:text-white',
          )
        }
      >
        <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="flex-1">{item.label}</span>
        {item.badge !== undefined && item.badge > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-bold text-white">
            {item.badge}
          </span>
        )}
      </NavLink>
    ));

  return (
    <div className="fixed inset-0 z-40 md:hidden">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={closeMobile}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') closeMobile();
        }}
        role="button"
        tabIndex={-1}
        aria-label="Close sidebar"
      />

      <aside className="fixed inset-y-0 left-0 z-50 flex w-56 flex-col bg-sidebar text-sidebar-foreground">
        <div className="flex items-center justify-between px-4 py-4">
          <img src={chartaiLogoLight} alt="ChartAI" className="h-7 w-auto" />
          <button
            type="button"
            onClick={closeMobile}
            className="flex items-center justify-center rounded-md p-1.5 text-sidebar-muted transition-colors hover:bg-sidebar-hover hover:text-white"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <nav aria-label="Dashboard navigation" className="flex-1 overflow-y-auto px-2">
          {SECTIONS.map((section, sIdx) => (
            <div key={`section-${sIdx}`}>
              {sIdx > 0 && <div className="my-2 mx-2 border-t border-sidebar-muted/20" />}
              {renderItems(section.items)}
            </div>
          ))}
        </nav>

        {/* Messages */}
        <div className="border-t border-sidebar-muted/20 px-2 py-2">
          <NavLink
            to="/dashboard/messages"
            onClick={closeMobile}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-sidebar-active text-white'
                  : 'text-sidebar-foreground/80 hover:bg-sidebar-hover hover:text-white',
              )
            }
          >
            <MessageCircle className="h-5 w-5 shrink-0" />
            <span className="flex-1">Messages</span>
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-bold text-white">
              3
            </span>
          </NavLink>
        </div>

        {/* Profile at bottom */}
        <div className="border-t border-sidebar-muted/20 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
              DS
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-white">Dr. Smith</p>
              <p className="truncate text-xs text-sidebar-muted">Provider</p>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
